/*
import {
  buildFacet,
  buildPager,
  buildResultsPerPage,
  buildResultTemplatesManager,
  buildSearchBox,
  buildSearchEngine,
  getOrganizationEndpoints,
  loadFieldActions,
} from "@coveo/headless"
import { buildProductRecommendationEngine } from "@coveo/headless/product-recommendation"
import { buildRecommendationEngine } from "@coveo/headless/recommendation"
*/
import { OverlayProvider } from "@react-aria/overlays"
import { enableContextDevTools as enableUFContextDevTools, Context as UFContext } from "@uniformdev/context"
import { UniformContext as UFReactContext } from "@uniformdev/context-react"
import DigitalDataLayer from "components/analytics/DigitalDataLayer"
import Didomi from "components/analytics/Didomi"
import * as components from "components/components"
import { ConfiguratorProvider } from "components/configurator-v3/context"
import Console from "components/console/Console"
import Dictionary from "components/dictionary/Dictionary"
import Env from "components/env/Env"
import Helmet from "components/helmet/Helmet"
import Layout from "components/layout/Layout"
import Locale from "components/locale/Locale"
import NavWrapper from "components/nav-v2/Wrapper"
import Navigation from "components/navigation/Navigation"
// import { SSRProvider } from "react-aria"
import { QuickViewsProvider } from "components/quickviews/context"
import SearchWrapper from "components/search/SearchWrapper"
import { StoryProvider } from "components/stories/context"
import Suspender from "components/suspender/Suspender"
import Theme from "components/theme/Theme"
import User from "components/user/User"
import Viewport from "components/viewport/Viewport"
import { XplitProvider } from "components/xplit/context"
import { PanelModalProvider } from "contexts/PanelModal"
import { Provider as WishlistProvider } from "contexts/Wishlist"
import reactDOM, { createRoot } from "react-dom/client"
import { HelmetProvider } from "react-helmet-async"
import { BrowserRouter } from "react-router-dom"
import { LastLocationProvider } from "react-router-last-location"
import reportWebVitals from "src/reportWebVitals"
import camelCase from "utils/camelCase"
import "focus-visible"
import Coveo from "components/coveo/Coveo"

const optionsForSearchBox = {
  options: {
    highlightOptions: {
      exactMatchDelimiters: {
        open: "<strong>",
        close: "</strong>",
      },
      correctionDelimiters: {
        open: "<i>",
        close: "</i>",
      },
    },
  },
}

global.UFContext = UFContext

global.accessTokens = {}
// Load correlation ID from localStorage on initialization
global.correlationId = localStorage.getItem("correlationId") || null

function isPageLoaded() {
  return document.readyState === "complete"
}

async function ensurePageLoaded() {
  if (isPageLoaded()) return
  await new Promise(resolve => {
    window.addEventListener("load", resolve, { once: true })
  })
}

async function uniform() {
  return JSON.parse(document.querySelector("#__INITIAL_MANIFEST")?.textContent ?? null) ?? Promise.resolve({ project: {} })
}

async function p13n([initialState, akamai, ufManifest]) {
  const quirks = {
    "aka-loc-country": akamai.realCountryCode || akamai.countryCode,
  }

  const sessionExpirationInSeconds = 2
  const ufContext = new UFContext({
    defaultConsent: true,
    manifest: ufManifest,
    plugins: [enableUFContextDevTools()],
    // visitLifespan: sessionExpirationInSeconds * 1000,
  })

  // console.log("uniform:context:update(%o)", { manifest: ufManifest, visitLifespan: sessionExpirationInSeconds * 1000 })
  ufContext.update({ quirks })
  return [initialState, akamai, ufContext]
}

/*
async function getCoveoSearchTokens(initialState, retryCount = 0) {
  // Ensure the page is fully loaded before requesting tokens
  if (!isPageLoaded()) {
    await ensurePageLoaded()
  }

  try {
    const url = new URL(
      `/api/catalog/${initialState.env.searchTokenRoute}/${initialState.env.searchTokenEndpoint}`,
      initialState?.env?.services || global.location.origin
    )

    const headers = {
      "Content-Type": "application/json",
      "x-timestamp": +new Date(),
    }

    // Add correlation ID to subsequent requests (V7-7177)
    if (global.correlationId) {
      headers["x-correlation-id"] = global.correlationId
    }

    const request = new Request(url, {
      method: "GET",
      credentials: initialState?.env?.withCredentials ? "include" : "same-origin",
      headers,
    })

    const response = await fetch(request)

    // Store correlation ID from response for future requests
    const correlationId = response.headers.get("x-correlation-id")
    if (correlationId && !global.correlationId) {
      global.correlationId = correlationId
      localStorage.setItem("correlationId", correlationId)
    }

    // Handle different response codes
    if (response.status === 503) {
      // Temporary service issues, retry after specified time (V7-7177)
      const retryAfter = Number.parseInt(response.headers.get("Retry-After") || "5")
      console.warn(`Service temporarily unavailable (503), retrying after ${retryAfter} seconds`)

      return new Promise((resolve, reject) => {
        setTimeout(async () => {
          try {
            const result = await getCoveoSearchTokens(initialState, retryCount + 1)
            resolve(result)
          } catch (err) {
            reject(err)
          }
        }, retryAfter * 1000)
      })
    }

    if (response.status === 502) {
      console.error("Token service down (502)")
      throw new Error("Token service unavailable")
    }

    if (response.status !== 200) {
      throw new Error(`Unexpected response status: ${response.status}`)
    }

    const body = await response.json()
    // Log failed tokens (V7-7177)
    const tokenFailed = response.headers.get("x-token-failed")
    const tokenSucceeded = response.headers.get("x-token-succeeded")

    if (tokenFailed) {
      console.warn("Failed to get tokens for keys:", tokenFailed)
    }

    return [
      body["rlxcom-grids"],
      body["rlxcom-web-search"],
      body["rlxcom-web-recommendation"],
      body["rlxcom_testimonees"],
      body["rlxcom_testimonees_recommendation"],
      body["rlxcom-plp"],
    ]
  } catch (err) {
    console.error("Error getting Coveo search tokens:", err)
    // No more fallback to fake tokens - throw the error
    throw err
  }
}

async function coveo([initialState, akamai, ufContext]) {
  const resultResponse = []

  const getReferrer = () => {
    if (initialState?.env?.hostname) {
      return initialState.env.hostname
    }
    return initialState?.env?.statics?.includes("rolex.cn") ? "rolex.cn" : "rolex.com"
  }

  let gridAccessToken
  let searchAccessToken
  let webRecommendationToken
  let testimoneesToken
  let testimoneesRecommendationToken
  let pipelineCoveoPLP

  try {
    ;[gridAccessToken, searchAccessToken, webRecommendationToken, testimoneesToken, testimoneesRecommendationToken, pipelineCoveoPLP] =
      await getCoveoSearchTokens(initialState)
  } catch (err) {
    console.error("Failed to get Coveo search tokens:", err)
    // If token service fails, we cannot initialize Coveo properly
    // Return early to prevent broken Coveo initialization
    return [initialState, akamai, ufContext]
  }

  global.accessTokens.pipelineWebsearch = searchAccessToken
  global.accessTokens.pipelineGrids = gridAccessToken
  global.accessTokens.pipelineWebRecommendation = webRecommendationToken
  global.accessTokens.pipelineTestimonees = testimoneesToken
  global.accessTokens.pipelineTestimoneesRecommendation = testimoneesRecommendationToken
  global.accessTokens.pipelineCoveoPLP = pipelineCoveoPLP // TODO: use a specific token for PLP

  global.coveoRecommendationEngine = buildProductRecommendationEngine({
    configuration: {
      organizationId: initialState.env.organizationId, //init.env
      accessToken: webRecommendationToken,
      organizationEndpoints: getOrganizationEndpoints(initialState.env.organizationId),
      pipeline: initialState.env.pipelineWebRecommendation,
      searchHub: initialState.env.pipelineWebRecommendation,
      search: {
        searchHub: initialState.env.pipelineWebRecommendation,
      },
      locale: initialState?.current_locale?.codes?.ws || "en_US",
    },
  })

  global.coveoTestimoneesRecommendationEngine = buildRecommendationEngine({
    configuration: {
      organizationId: initialState.env.organizationId, //init.env
      accessToken: testimoneesRecommendationToken,
      organizationEndpoints: getOrganizationEndpoints(initialState.env.organizationId),
      // platformUrl: "https://platform-eu.cloud.coveo.com", // deprecated
      pipeline: initialState.env.pipelineTestimoneesRecommendation,
      searchHub: initialState.env.pipelineTestimoneesRecommendation,
      search: {
        searchHub: initialState.env.pipelineTestimoneesRecommendation,
      },
      locale: initialState?.current_locale?.codes?.ws || "en_US",
    },
  })

  const fieldsToInclude = [
    "rlx_contenttype",
    "rlx_rmc",
    "rlx_title",
    "rlx_family",
    "rlx_family_facet",
    "rlx_case_title",
    "rlx_newmodelselection",
    "rlx_familycode",
    "rlx_description",
    "rlx_searchimage",
    "rlx_search_section",
    "rlx_search_title",
    "rlx_path",
  ]
  setTimeout(() => {
    const { registerFieldsToInclude } = loadFieldActions(global.coveoHeadlessEngine)
    global.coveoHeadlessEngine.dispatch(registerFieldsToInclude(fieldsToInclude))
  }, 100)
  global.coveoHeadlessEngine = buildSearchEngine({
    configuration: {
      organizationId: initialState.env.organizationId, //init.env
      accessToken: global.accessTokens.pipelineWebsearch,
      organizationEndpoints: getOrganizationEndpoints(initialState.env.organizationId),
      search: {
        searchHub: initialState.env.pipeline,
        preprocessSearchResponseMiddleware: response => {
          resultResponse.push(...response.body.results)
          return response
        },
      },
      preprocessRequest: (request, clientOrigin) => {
        const referrer = getReferrer()
        if (clientOrigin === "analyticsFetch") {
          const body = request.body
          const jsonBody = JSON.parse(body)
          const actionCause = jsonBody.actionCause
          if (actionCause === "documentOpen") {
            const documentId = jsonBody.customData.contentIDValue

            let customData = {
              ...jsonBody.customData,
              product_brand: "rolex",
              siteName: "rolex.com",
            }

            const matchedResult = resultResponse.find(result => documentId === result.raw.permanentid)

            if (matchedResult) {
              if (matchedResult.raw.rlx_family_facet) {
                customData = {
                  ...customData,
                  product_variant: matchedResult.raw.rlx_family_facet[0],
                  product_name: matchedResult.title,
                  product_price: null,
                  product_category: matchedResult.raw.rlx_familycode,
                }
              }
            }
            const newJsonBody = {
              ...jsonBody,
              referrer,
              customData,
            }
            const newRequest = {
              ...request,
              body: JSON.stringify(newJsonBody),
            }
            return newRequest
          }
        }
        const body = request.body
        const jsonBody = JSON.parse(body)
        const newJsonBody = {
          ...jsonBody,
          originLevel2: (() => {
            // this is a temporary solution to get the correct originLevel2, we need more info from VTC & Coveo
            if (jsonBody.sourceName?.includes("rlx_catalog")) {
              return "watches"
            }
            if (jsonBody.sourceName?.includes("articles")) {
              return "articles"
            }
            return jsonBody.originLevel2
          })(),
          referrer,
        }
        const newRequest = {
          ...request,
          body: JSON.stringify(newJsonBody),
        }
        return newRequest
      },
    },
  })
  global.coveoSearchBox = buildSearchBox(global.coveoHeadlessEngine, optionsForSearchBox)
  global.coveoResultList = buildResultsPerPage(global.coveoHeadlessEngine, {
    initialState: { numberOfResults: 1000 },
  })
  global.coveoFacet = buildFacet(global.coveoHeadlessEngine, {
    options: { field: "source" },
  })
  global.coveoPager = buildPager(global.coveoHeadlessEngine, {
    options: { numberOfPages: 3 },
  }) //todosearch: remove ?
  global.coveoResultTemplatesManager = buildResultTemplatesManager(global.coveoHeadlessEngine)

  // re-execute this function every 24 hours so we keep have a fresh accesstoken
  setInterval(async () => {
    try {
      await coveo([initialState, akamai, ufContext])
    } catch (err) {
      console.error("Failed to refresh Coveo tokens:", err)
    }
  }, 1000 * 60 * 60 * 24)

  return [initialState, akamai, ufContext]
}
*/

async function initialState() {
  return Promise.resolve(
    JSON.parse(document.querySelector("#__INITIAL_STATE")?.textContent ?? null) ??
      Promise.resolve(new URL(`${global.location.pathname.replace(/.html$/, "")}.initial.json`, `${global.location.protocol}//${global.location.host}`))
        .then(url => new Request(url))
        .then(request => fetch(request))
        .then(response => response.json())
  )
    .then(initialState =>
      Promise.all([
        Promise.resolve(initialState),
        Promise.all(
          (initialState?.preload ?? []).map(component => {
            try {
              const Component = components[component]
              const lazy = Component?.$$typeof === Symbol.for("react.lazy")
              if (!lazy) return Promise.resolve()
              return Component._init(Component._payload)
            } catch (errOrPromise) {
              if (typeof errOrPromise?.then === "function") return errOrPromise.catch(err => console.error(err))
              console.error(errOrPromise)
              return Promise.resolve()
            }
          })
        ),
      ])
    )
    .then(([initialState]) => initialState)
}

async function akamai() {
  const cc = document.documentElement.getAttribute("data-cc") || null
  const _saved = sessionStorage.getItem("reqcontext") && JSON.parse(sessionStorage.getItem("reqcontext"))
  const saved = _saved && _saved.countryCode === cc ? _saved : null

  const url = new URL("/ak-getreqcontext.json", process.env.NODE_ENV === "development" ? "https://www.rolex.com" : global.location.origin)

  return (saved ? Promise.resolve(saved) : Promise.reject())
    .catch(() =>
      fetch(url)
        .then(response => response.json())
        .catch(err => {
          console.error(err)
          return { reqcontext: {} }
        })
        .then(({ reqcontext }) => reqcontext)
        .then(reqcontext => {
          const ctx = Object.keys(reqcontext).reduce((ctx, key) => {
            const cckey = camelCase(key)
            try {
              ctx[cckey] = /*cckey === "countryCode" && cc ? cc :*/ JSON.parse(reqcontext[key])
            } catch {
              ctx[cckey] = /*cckey === "countryCode" && cc ? cc :*/ reqcontext[key]
            }
            return ctx
          }, {})
          return ctx
        })
        .then(reqcontext => {
          sessionStorage.setItem("reqcontext", JSON.stringify(reqcontext))
          return reqcontext
        })
    )
    .then(reqcontext => {
      //console.log("ak:ctx(%o)", { cache: !!saved, reqcontext })
      reqcontext.realCountryCode = document.documentElement.getAttribute("data-rcc") || reqcontext.countryCode.toUpperCase()
      reqcontext.countryCode = document.documentElement.getAttribute("data-cc") || reqcontext.realCountryCode
      return reqcontext
    })
}
async function main([initialState, akamai, ufContext], forceRender) {
  if (process.env.NODE_ENV === "development")
    console.debug("initialState/ak/uf, %o", {
      initialState,
      akamai,
      ufContext,
    })

  const hydrate = !forceRender && document.querySelector("meta[name=hydrate]")
  const target = document.querySelector(hydrate ? hydrate.getAttribute("content") : "#root")

  const AppBootstrap = (
    // <SSRProvider>
    <HelmetProvider>
      <Console>
        <UFReactContext context={ufContext}>
          <Suspender>
            <Coveo initialState={initialState}>
              <BrowserRouter>
                <LastLocationProvider>
                  <Locale initialState={initialState}>
                    <User reqcontext={akamai}>
                      <Dictionary initialState={initialState}>
                        <Env initialState={initialState}>
                          {/* <QuantCast initialState={initialState} /> */}
                          <DigitalDataLayer version={document.documentElement.getAttribute("data-version")} initialState={initialState}>
                            {/* <SearchWrapper>*/}
                            <NavWrapper>
                              <Navigation initialState={initialState}>
                                <Helmet initialState={initialState}>
                                  <Viewport>
                                    <Theme>
                                      <Didomi />
                                      <OverlayProvider>
                                        <QuickViewsProvider>
                                          <StoryProvider>
                                            <WishlistProvider>
                                              <XplitProvider initialState={initialState}>
                                                <ConfiguratorProvider>
                                                  <PanelModalProvider>
                                                    <Layout initialState={initialState} />
                                                  </PanelModalProvider>
                                                </ConfiguratorProvider>
                                              </XplitProvider>
                                            </WishlistProvider>
                                          </StoryProvider>
                                        </QuickViewsProvider>
                                      </OverlayProvider>
                                    </Theme>
                                  </Viewport>
                                </Helmet>
                              </Navigation>
                            </NavWrapper>
                            {/* </SearchWrapper>*/}
                          </DigitalDataLayer>
                        </Env>
                      </Dictionary>
                    </User>
                  </Locale>
                </LastLocationProvider>
              </BrowserRouter>
            </Coveo>
          </Suspender>
        </UFReactContext>
      </Console>
    </HelmetProvider>
    // </SSRProvider>
  )

  /**
   * hydratation breaks react fast refresh in dev mode
   */

  if (process.env.NODE_ENV === "production" && hydrate) reactDOM.hydrateRoot(target, AppBootstrap)
  else {
    const url = new URL(global.location)
    if (url.searchParams.get("hydrate") === "1") {
      reactDOM.hydrateRoot(target, AppBootstrap)
    } else createRoot(target).render(AppBootstrap)
  }
}

Promise.all([initialState(), akamai(), uniform()])
  .then(p13n)
  // .then(coveo)
  .then(main)
  .catch(err => {
    //TODO ?
    console.error(err)
  })

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
